import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { Location } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { SensorManageService } from '../../../core/services/sensor-manage.service';
import { SiteService } from '../../../core/services/site.service';
import { LoadingService } from '../../../core/services/loading.service';
import { NotificationService } from '../../../core/services/notification.service';
import { PositionNameDialogComponent } from '../position-name-dialog/position-name-dialog';
import type { Sensor, SensorCategoryName } from '../../../shared/interfaces';

const SENSOR_LIMITS: Record<SensorCategoryName, number | null> = {
  WBGT: 2,
  WIND: 1,
  NOISE: 1,
  PH: 1,
  TILT: null
};

@Component({
  selector: 'app-site-sensor-manage',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatDividerModule
  ],
  templateUrl: './site-sensor-manage.html',
  styleUrl: './site-sensor-manage.scss'
})
export class SiteSensorManageComponent {
  private readonly sensorManageService = inject(SensorManageService);
  private readonly siteService = inject(SiteService);
  private readonly loadingService = inject(LoadingService);
  private readonly notificationService = inject(NotificationService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly location = inject(Location);

  public id = input.required<string>();

  public readonly siteName = computed(() => {
    const detail = this.siteService.getSiteDetail(this.id());
    return detail?.name ?? '読込中...';
  });

  public readonly assignedSensors = computed(() => 
    this.sensorManageService.allSensors().filter(s => s.siteId === this.id())
  );

  public readonly unassignedSensors = computed(() => {
    const siteDetail = this.siteService.getSiteDetail(this.id());
    const siteOrgId = siteDetail?.organizationId;
    return this.sensorManageService.allSensors().filter(s => 
      !s.siteId && s.organizationId === siteOrgId
    );
  });

  private readonly categoryCounts = computed(() => {
    const counts = Object.keys(SENSOR_LIMITS).reduce((acc, key) => {
      acc[key as SensorCategoryName] = 0;
      return acc;
    }, {} as Record<SensorCategoryName, number>);
    
    for (const sensor of this.assignedSensors()) {
      counts[sensor.categoryName]++;
    }
    return counts;
  });

  constructor() {
    effect((onCleanup) => {
      const isLoading = this.sensorManageService.isLoading() || this.siteService.isLoading();
      if (isLoading) {
        this.loadingService.show();
        onCleanup(() => this.loadingService.hide());
      }
    });

    effect(() => {
      const siteErr = this.siteService.error();
      if (siteErr) this.notificationService.error(siteErr);
      
      const sensorErr = this.sensorManageService.error();
      if (sensorErr) this.notificationService.error(sensorErr);
    });
  }

  public canAssign(category: SensorCategoryName): boolean {
    const limit = SENSOR_LIMITS[category];
    if (limit === null) return true;
    return this.categoryCounts()[category] < limit;
  }

  public async assign(sensorId: string, category: SensorCategoryName): Promise<void> {
    if (!this.canAssign(category)) {
      this.snackBar.open(`${category} センサーはこれ以上追加できません`, '閉じる', { duration: 3000 });
      return;
    }

    const positionName = await this.openPositionNameDialog('設置場所を入力');
    if (positionName === undefined) return;
    
    this.loadingService.show();
    try {
      await this.sensorManageService.assignSensor(sensorId, this.id(), positionName);
      this.snackBar.open('センサーを紐付けました', '閉じる', { duration: 3000 });
    } catch (err) {
      this.snackBar.open('センサーの紐付けに失敗しました', '閉じる', { duration: 3000 });
    } finally {
      this.loadingService.hide();
    }
  }

  public async editPositionName(sensor: Sensor): Promise<void> {
    const positionName = await this.openPositionNameDialog('設置場所を編集', sensor.positionName);
    if (positionName === undefined || positionName === (sensor.positionName ?? '')) return;

    this.loadingService.show();
    try {
      await this.sensorManageService.updatePositionName(sensor.id, positionName);
      this.snackBar.open('設置場所を更新しました', '閉じる', { duration: 3000 });
    } catch (err) {
      this.snackBar.open('設置場所の更新に失敗しました', '閉じる', { duration: 3000 });
    } finally {
      this.loadingService.hide();
    }
  }

  private async openPositionNameDialog(title: string, positionName?: string): Promise<string | undefined> {
    const dialogRef = this.dialog.open(PositionNameDialogComponent, {
      width: '420px',
      data: { title, positionName }
    });
    return firstValueFrom(dialogRef.afterClosed());
  }

  public async unassign(sensorId: string): Promise<void> {
    this.loadingService.show();
    try {
      await this.sensorManageService.unassignSensor(sensorId);
      this.snackBar.open('センサーの紐付けを解除しました', '閉じる', { duration: 3000 });
    } catch (err) {
      this.snackBar.open('紐付け解除に失敗しました', '閉じる', { duration: 3000 });
    } finally {
      this.loadingService.hide();
    }
  }

  public goBack(): void {
    this.location.back();
  }
}
